import { useEffect, useState } from 'react';
import { DocumentTranscriptUtteranceV1 } from '@utils/types/editors/transcripts/transcript.ts';
import SharedScriptixEditor from '@components/editors/shared-editor/shared-transcript/shared-scriptix-editor.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import cn from 'classnames';

interface TranscriptRowProps {
  index: number;
  paragraph: DocumentTranscriptUtteranceV1;
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
}

export const SharedTranscriptRow = ({
  index,
  paragraph,
  onWordOutOfView,
  onWordInView
}: TranscriptRowProps) => {
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { theme } = useThemeContext();
  const { selectedParagraphs, toggleParagraphSelection } =
    useSharedTranscript();

  const isSelected = selectedParagraphs.includes(index);

  // Theme-aware colors
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const activeBorderColor = pick('rgb(191 219 254)', 'rgb(59 130 246)'); // blue-200 / blue-500
  const activeBgColor = pick('rgb(239 246 255)', 'rgba(30, 58, 138, 0.2)'); // blue-50 / blue-900/20
  const selectedBorderColor = pick('rgb(134 239 172)', 'rgb(34 197 94)'); // green-300 / green-500
  const selectedBgColor = pick('rgb(220 252 231)', 'rgba(21, 128, 61, 0.2)'); // green-100 / green-800/20
  const hoverBorderColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700
  const hoverBgColor = pick('rgb(249 250 251)', 'rgba(31, 41, 55, 0.3)'); // gray-50 / gray-800/30

  useEffect(() => {
    const handleActiveRow = (e: Event) => {
      const { index: activeIndex } = (e as CustomEvent).detail || {};
      setIsActive(activeIndex === index);
    };

    window.addEventListener(
      'transcript:activeRow',
      handleActiveRow as EventListener
    );
    return () => {
      window.removeEventListener(
        'transcript:activeRow',
        handleActiveRow as EventListener
      );
    };
  }, [index]);

  const handleRowClick = (e: React.MouseEvent) => {
    // Handle multi-selection with Shift key
    if (e.shiftKey) {
      e.preventDefault();
      toggleParagraphSelection(index, true);
    } else {
      // Normal click - set as active row and clear selection
      window.dispatchEvent(
        new CustomEvent('transcript:activeRow', { detail: { index } })
      );
    }
  };

  const handleRowFocus = () => {
    // Set as active row on focus (keyboard navigation)
    window.dispatchEvent(
      new CustomEvent('transcript:activeRow', { detail: { index } })
    );
  };

  const getStyles = () => {
    if (isSelected) {
      return {
        borderColor: selectedBorderColor,
        backgroundColor: selectedBgColor
      };
    }
    if (isActive) {
      return {
        borderColor: activeBorderColor,
        backgroundColor: activeBgColor
      };
    }
    if (isHovered) {
      return {
        borderColor: hoverBorderColor,
        backgroundColor: hoverBgColor
      };
    }
    return {
      borderColor: 'transparent',
      backgroundColor: 'transparent'
    };
  };

  return (
    <div
      onClick={handleRowClick}
      onFocus={handleRowFocus}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'rounded-2xl transition-all duration-200 cursor-pointer',
        'border p-2 pb-3',
        isActive && 'shadow-md',
        isSelected && 'shadow-sm'
      )}
      style={getStyles()}
    >
      <SharedScriptixEditor
        index={index}
        paragraph={paragraph}
        onWordOutOfView={onWordOutOfView}
        onWordInView={onWordInView}
      />
    </div>
  );
};
